import { Avatar, Box, Grid, GridProps, styled, Typography } from '@mui/material';
import { PopoverTwoColumns } from 'redesign/popover-two-column-table/popover-two-columns';
import { getFullNameWithInfix } from 'hq/components/views/flexWorkers/utils';
import { useRef, useState } from 'react';
import { groupBy } from 'lodash';
import { DateTime } from 'luxon';
import { getColorForAvailabilityType } from './helpers/get-color-for-availability-type';
import {
  getUnavailabilityColor,
  shouldHideUnavailabilityCompensation,
} from './helpers/get-unavailability-color';
import { useGetPopoverInfoRows } from './hooks/use-get-popover-info-rows';
import { AvailabilityParsedItem } from './availability-calendar.types';
import { SegmentLaneConfig } from './availability-grid-lanes.types';

export const getAvailabilityGridLanes = (
  availabilityBlocks: AvailabilityParsedItem[]
): SegmentLaneConfig[] => {
  const segmentsByType = groupBy(
    [...availabilityBlocks].sort(compareByUtcStart),
    (segment) => segment.type
  );
  const hideCompensation = shouldHideUnavailabilityCompensation(
    availabilityBlocks[0]?.workerForPopover
  );
  const paidSegments = segmentsByType['unavailablePaid'] ?? [];
  const unpaidSegments = segmentsByType['unavailableUnpaid'] ?? [];
  const unavailabilityLanes: SegmentLaneConfig[] = hideCompensation
    ? [
        {
          segments: [...paidSegments, ...unpaidSegments].sort(compareByUtcStart),
          Cell: StyledUnavailabilityCell,
          testIdPrefix: 'unavailable-block',
        },
      ]
    : [
        {
          segments: paidSegments,
          Cell: StyledUnavailabilityCell,
          testIdPrefix: 'unavailable-paid-block',
          clickable: true,
        },
        {
          segments: unpaidSegments,
          Cell: StyledUnavailabilityCell,
          testIdPrefix: 'unavailable-unpaid-block',
          clickable: true,
        },
      ];

  return [
    {
      segments: segmentsByType['available'] ?? [],
      Cell: StyledAvailabilityCell,
      testIdPrefix: 'availability-block',
      clickable: true,
      keepAlignment: true,
    },
    ...unavailabilityLanes,
    {
      segments: segmentsByType['pending'] ?? [],
      Cell: StyledPendingCell,
      testIdPrefix: 'pending-block',
      keepAlignment: true,
    },
    {
      segments: segmentsByType['tentative'] ?? [],
      Cell: StyledTentativeCell,
      testIdPrefix: 'tentative-block',
      keepAlignment: true,
    },
    {
      segments: segmentsByType['blocked'] ?? [],
      Cell: StyledBlockedCell,
      testIdPrefix: 'blocked-block',
      keepAlignment: true,
    },
  ];
};

export const SegmentLane = ({
  segments,
  Cell,
  testIdPrefix,
  clickable,
  keepAlignment,
  handleClick,
}: SegmentLaneConfig & { handleClick: (...args: any[]) => void }) => {
  if (segments.length === 0) {
    return keepAlignment ? <FillerBlock /> : null;
  }

  const worker = segments[0].workerForPopover;

  return (
    <Cell
      data={segments}
      item
      data-testid={`${testIdPrefix}-${worker?.workerId}`}
      onClick={
        clickable
          ? () => handleClick(segments, getFullNameWithInfix(worker), worker?.workerId)
          : undefined
      }
    />
  );
};

const compareByUtcStart = (a: AvailabilityParsedItem, b: AvailabilityParsedItem) =>
  DateTime.fromISO(a.utcStartDateTime).toMillis() - DateTime.fromISO(b.utcStartDateTime).toMillis();

const AvailabilityCell = ({ data, ...props }: { data: AvailabilityParsedItem[] } & GridProps) => {
  const popoverInfoRows = useGetPopoverInfoRows(data);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const closeTimer = useRef<number | null>(null);

  const startHoverEffect = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setAnchorEl(e.currentTarget);
  };

  const stopHoverEffect = () => {
    closeTimer.current = window.setTimeout(() => {
      setAnchorEl(null);
    }, 100);
  };

  return (
    <>
      <Grid
        {...props}
        onMouseEnter={startHoverEffect}
        onMouseLeave={stopHoverEffect}
        sx={{ height: '100%', width: '100%' }}
      />
      <PopoverTwoColumns
        header={
          <>
            <WrapperBox>
              <StyledAvatarContainer>
                <Avatar src={data[0].workerForPopover?.profilePictureUrl} alt="Profile Picture" />
              </StyledAvatarContainer>
            </WrapperBox>
            <NameBox>
              <Typography variant="body1" fontWeight={600} noWrap>
                {getFullNameWithInfix(data[0].workerForPopover)}
              </Typography>
            </NameBox>
          </>
        }
        data={popoverInfoRows}
        maxWidth={350}
        maxHeight={400}
        anchorEl={anchorEl}
      />
    </>
  );
};

const StyledAvailabilityCell = styled(AvailabilityCell)(({ theme }) => ({
  backgroundColor: getColorForAvailabilityType('available', 0.2),
  ':hover': {
    backgroundColor: getColorForAvailabilityType('available', 1),
    border: '1px solid',
    borderColor: theme.palette.lwPrimaryAccentLight?.[5],
    boxShadow: `0 12px 32px ${getColorForAvailabilityType('available', 0.5)}`,
  },
  minHeight: '15px',
}));

const StyledBlockedCell = styled(AvailabilityCell)(({ theme }) => ({
  backgroundColor: getColorForAvailabilityType('blocked', 0.1),
  ':hover': {
    backgroundColor: getColorForAvailabilityType('blocked', 0.4),
    border: '1px solid',
    borderColor: theme.palette.lwPrimaryAccentLight?.[5],
    boxShadow: `0 12px 32px ${getColorForAvailabilityType('blocked', 0.5)}`,
  },
  minHeight: '15px',
}));

const StyledTentativeCell = styled(AvailabilityCell)(({ theme }) => ({
  backgroundColor: getColorForAvailabilityType('tentative', 0.4),
  ':hover': {
    backgroundColor: getColorForAvailabilityType('tentative', 1),
    border: '1px solid',
    borderColor: theme.palette.lwPrimaryAccentLight?.[5],
    boxShadow: `0 12px 32px ${getColorForAvailabilityType('tentative', 0.5)}`,
  },
  minHeight: '15px',
}));

const StyledPendingCell = styled(AvailabilityCell)(({ theme }) => ({
  backgroundColor: getColorForAvailabilityType('pending', 0.4),
  ':hover': {
    backgroundColor: getColorForAvailabilityType('pending', 1),
    border: '1px solid',
    borderColor: theme.palette.lwPrimaryAccentLight?.[5],
    boxShadow: `0 12px 32px ${getColorForAvailabilityType('pending', 0.5)}`,
  },
  minHeight: '15px',
}));

const StyledUnavailabilityCell = styled(AvailabilityCell)(({ theme }) => ({
  backgroundColor: getUnavailabilityColor(theme),
  ':hover': {
    backgroundColor: getUnavailabilityColor(theme),
    border: '1px solid',
    borderColor: theme.palette.lwPrimaryAccentLight?.[5],
    boxShadow: `0 12px 32px ${theme.palette.lwBlack[100]}`,
  },
  minHeight: '15px',
}));

const FillerBlock = styled(Box)({
  height: '15px',
});

const StyledAvatarContainer = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
}));

const WrapperBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '40%',
}));

const NameBox = styled(Box)(() => ({
  maxWidth: 300,
  minWidth: 0,
}));
